import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Checkbox, Label } from "@devhop/ui";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { useMemo, useState } from "react";

type PackageTemplateOption = {
  id: string;
  name: string;
  totalSessions: number;
  validDays: number;
};

type CampEventPackageTemplatesEditorProps = {
  selectedIds: string[];
  availableTemplates: PackageTemplateOption[];
  disabled?: boolean;
  onChange: (ids: string[]) => void;
  onViewTemplate?: (id: string) => void;
};

export function CampEventPackageTemplatesEditor({
  selectedIds,
  availableTemplates,
  disabled,
  onChange,
  onViewTemplate,
}: CampEventPackageTemplatesEditorProps) {
  const [pickId, setPickId] = useState("");

  const templateMap = useMemo(
    () => new Map(availableTemplates.map((t) => [t.id, t])),
    [availableTemplates],
  );

  const unselectedOptions = useMemo(
    () =>
      availableTemplates
        .filter((t) => !selectedIds.includes(t.id))
        .map((t) => ({
          value: t.id,
          label: `${t.name} (${t.totalSessions} ຄັ້ງ · ${t.validDays} ວັນ)`,
        })),
    [availableTemplates, selectedIds],
  );

  const handleAdd = () => {
    if (!pickId || selectedIds.includes(pickId)) return;
    onChange([...selectedIds, pickId]);
    setPickId("");
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((item) => item !== id));
  };

  const handleToggle = (id: string, checked: boolean) => {
    if (checked) {
      if (!selectedIds.includes(id)) onChange([...selectedIds, id]);
      return;
    }
    handleRemove(id);
  };

  return (
    <div className="space-y-4">
      {selectedIds.length > 0 ? (
        <ul className="space-y-2">
          {selectedIds.map((id) => {
            const template = templateMap.get(id);
            return (
              <li
                key={id}
                className="flex items-center justify-between gap-3 rounded-lg border bg-muted/20 px-3 py-2"
              >
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <Checkbox
                    checked
                    disabled={disabled}
                    onCheckedChange={(v) => handleToggle(id, v === true)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-sm">
                      {template?.name ?? id}
                    </p>
                    {template ? (
                      <p className="text-muted-foreground text-xs">
                        {template.totalSessions} ຄັ້ງ · {template.validDays}{" "}
                        ວັນ
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  {onViewTemplate ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={disabled}
                      onClick={() => onViewTemplate(id)}
                    >
                      ເບິ່ງ
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-destructive"
                    disabled={disabled}
                    onClick={() => handleRemove(id)}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-muted-foreground text-sm">
          ຍັງບໍ່ໄດ້ຜູກແພັກເກດ — ເພີ່ມຢ່າງໜ້ອຍ 1 ແມ່ແບບ
        </p>
      )}

      {unselectedOptions.length > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-2">
            <Label>ເພີ່ມແມ່ແບບແພັກເກດ</Label>
            <SimpleSelect
              value={pickId}
              onValueChange={setPickId}
              disabled={disabled}
              placeholder="— ເລືອກແມ່ແບບ —"
              className="w-full"
              options={unselectedOptions}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || !pickId}
            onClick={handleAdd}
            className="shrink-0"
          >
            <PlusIcon className="size-4" />
            ເພີ່ມ
          </Button>
        </div>
      ) : null}
    </div>
  );
}
